import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useQuery, useMutation, gql } from '@apollo/client'
import './ProductsPage.css'
import { getUserDisplayName } from '../utils/userDisplay.js'

const GET_OPERATORS = gql`
  query GetOperators {
    getOperators {
      id
      name
      code
      logo
    }
  }
`

const GET_PLANS = gql`
  query GetPlans($operatorId: ID) {
    getPlans(operatorId: $operatorId) {
      id
      operatorId
      operatorName
      amount
      validity
      description
      benefits
      talkTime
    }
  }
`

const CREATE_ORDER = gql`
  mutation CreateOrder($phone: String!, $planId: ID!, $amount: Float!) {
    createOrder(phone: $phone, planId: $planId, amount: $amount) {
      id
      orderId
      userId
      amount
      phone
      status
      createdAt
    }
  }
`

function ProductsPage({ user, isLoggedIn }) {
  const [selectedOperator, setSelectedOperator] = useState('')
  const [selectedPlan, setSelectedPlan] = useState('')
  const [rechargePhone, setRechargePhone] = useState(user?.phone || '')
  const navigate = useNavigate()
  const location = useLocation()

  const { data: operatorsData, loading: operatorsLoading } = useQuery(GET_OPERATORS)
  const { data: plansData, loading: plansLoading } = useQuery(GET_PLANS, {
    variables: { operatorId: selectedOperator },
    skip: !selectedOperator
  })

  const [createOrder, { loading: createLoading }] = useMutation(CREATE_ORDER, {
    onCompleted: (data) => {
      navigate(`/payment/${data.createOrder.orderId}`)
    },
    onError: (error) => {
      alert(`Error creating order: ${error.message}`)
    }
  })

  const handleProceedToPayment = () => {
    if (!isLoggedIn) {
      alert('Please login to proceed')
      navigate('/', { state: { from: location } })
      return
    }

    if (!rechargePhone || rechargePhone.length !== 10) {
      alert('Please enter a valid 10-digit recharge number')
      return
    }

    if (!selectedPlan) {
      alert('Please select a plan')
      return
    }

    const plan = plansData?.getPlans.find(p => p.id === selectedPlan)
    if (plan) {
      createOrder({
        variables: {
          phone: rechargePhone,
          planId: selectedPlan,
          amount: plan.amount
        }
      })
    }
  }

  return (
    <div className="products-page">
      <div className="container">
        <div className="products-header">
          <span className="products-kicker">Mobile recharge</span>
          <h1>Choose a recharge plan</h1>
          <p>Welcome, {getUserDisplayName(user)}. Select your operator, compare plans, and recharge in a few simple steps.</p>
          <div className="service-pills" aria-label="MoRecharz services">
            <span><span className="material-icons">phone_android</span> Mobile recharge</span>
            <span><span className="material-icons">receipt_long</span> Utility bill payments</span>
          </div>
          <div className="plans-notice">
            <span className="material-icons">info</span>
            <span>Plans shown here are for testing while live recharge plans are being prepared.</span>
          </div>
        </div>

        <div className="target-number-card">
          <div className="target-number-heading">
            <span className="material-icons">smartphone</span>
            <div>
              <label htmlFor="recharge-phone">Mobile number to recharge</label>
              <p>Enter the number that should receive the recharge.</p>
            </div>
          </div>
          <input
            id="recharge-phone"
            type="tel"
            value={rechargePhone}
            onChange={(e) => setRechargePhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            placeholder="Enter 10-digit number to recharge"
            maxLength="10"
          />
        </div>

        <div className="products-container">
          {/* Operators Section */}
          <div className="operators-section">
            <div className="section-heading-row">
              <div>
                <span className="section-eyebrow">Step 1</span>
                <h3>Choose your operator</h3>
              </div>
              <span className="section-helper">Select one to see available plans</span>
            </div>
            {operatorsLoading ? (
              <div className="loading">
                <div className="spinner"></div>
                <p>Loading operators...</p>
              </div>
            ) : (
              <div className="operators-grid">
                {operatorsData?.getOperators.map(operator => (
                  <div
                    key={operator.id}
                    className={`operator-card ${selectedOperator === operator.id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedOperator(operator.id)
                      setSelectedPlan('')
                    }}
                  >
                    <div className="operator-logo">
                      <span className="material-icons">phone_android</span>
                    </div>
                    <div className="operator-name">{operator.name}</div>
                    <div className="operator-badge">Select</div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Plans Section */}
          {selectedOperator && (
            <div className="plans-section">
              <div className="section-heading-row">
                <div>
                  <span className="section-eyebrow">Step 2</span>
                  <h3>{operatorsData?.getOperators.find(o => o.id === selectedOperator)?.name} recharge plans</h3>
                </div>
                <span className="section-helper">Choose a plan that fits your usage</span>
              </div>
              {plansLoading ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Loading plans...</p>
                </div>
              ) : (
                <div className="plans-list">
                  {plansData?.getPlans.length > 0 ? (
                    plansData.getPlans.map(plan => (
                      <div
                        key={plan.id}
                        className={`plan-card ${selectedPlan === plan.id ? 'selected' : ''}`}
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        <div className="plan-header">
                          <div className="plan-amount">₹{plan.amount}</div>
                          <div className="plan-validity">{plan.validity}</div>
                        </div>
                        
                        {plan.talkTime && (
                          <div className="plan-info">
                            <span className="material-icons">data_usage</span>
                            <span>{plan.talkTime}</span>
                          </div>
                        )}
                        
                        <div className="plan-benefits">
                          <span className="material-icons">check_circle</span>
                          <span>{plan.benefits}</span>
                        </div>
                        
                        <button 
                          className={`select-plan-btn ${selectedPlan === plan.id ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation()
                            setSelectedPlan(plan.id)
                          }}
                        >
                          {selectedPlan === plan.id ? '✓ Selected' : 'Select Plan'}
                        </button>
                      </div>
                    ))
                  ) : (
                    <p className="no-plans">No plans available for this operator.</p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        {selectedPlan && (
          <div className="action-section">
            <div className="plan-summary">
              <div className="summary-item">
                <span>User:</span>
                <strong>{getUserDisplayName(user, 'Logged in user')}</strong>
              </div>
              <div className="summary-item">
                <span>Recharge Number:</span>
                <strong>{rechargePhone}</strong>
              </div>
              <div className="summary-item">
                <span>Operator:</span>
                <strong>{operatorsData?.getOperators.find(o => o.id === selectedOperator)?.name}</strong>
              </div>
              <div className="summary-item">
                <span>Plan Amount:</span>
                <strong>₹{plansData?.getPlans.find(p => p.id === selectedPlan)?.amount}</strong>
              </div>
              <div className="summary-item">
                <span>Validity:</span>
                <strong>{plansData?.getPlans.find(p => p.id === selectedPlan)?.validity}</strong>
              </div>
            </div>
            
            <div className="action-buttons">
              <button 
                className="btn-primary"
                onClick={handleProceedToPayment}
                disabled={createLoading}
              >
                {createLoading ? 'Processing...' : 'Proceed to Payment'}
              </button>
              <button 
                className="btn-secondary"
                onClick={() => {
                  setSelectedOperator('')
                  setSelectedPlan('')
                }}
              >
                Clear Selection
              </button>
            </div>
          </div>
        )}

        {!selectedPlan && (
          <div className="empty-state">
            <span className="material-icons">shopping_cart</span>
            <p>Select an operator and plan to get started</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductsPage
